import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, LayoutTemplate, Trash2, Globe, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { pprwAPI } from '../../services/api';

// Pick a saved template and spin up a new workshop (with its time-zone sessions) in one click.
export function WorkshopTemplateDialog({ open, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTemplates(await pprwAPI.workshops.templates.getAll()); }
    catch (_e) { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setSelectedId(null); setTitle(''); setStartDate(''); load(); } }, [open, load]);

  const selected = templates.find(t => t.id === selectedId);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this template?')) return;
    try { await pprwAPI.workshops.templates.delete(id); toast.success('Template deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const handleInstantiate = async () => {
    if (!selectedId) { toast.error('Select a template'); return; }
    setBusy(true);
    try {
      const w = await pprwAPI.workshops.templates.instantiate(selectedId, { start_date: startDate || null, title: title || null });
      toast.success(`Workshop created with ${w.session_count} session${w.session_count !== 1 ? 's' : ''}`);
      onCreated(w.id); onClose();
    } catch (_e) { toast.error('Failed to create from template'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="workshop-template-dialog">
        <DialogHeader>
          <DialogTitle>Create Workshop from Template</DialogTitle>
          <DialogDescription>Pick a template to spin up a workshop and all its time-zone sessions in one click. Save a workshop as a template from its detail page.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <LayoutTemplate className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No templates yet. Open a workshop and click “Save as Template”.
            </div>
          ) : templates.map(t => (
            <div key={t.id} role="button" tabIndex={0} onClick={() => setSelectedId(t.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(t.id); } }}
              className={`w-full text-left rounded-lg border p-3 transition-all cursor-pointer ${selectedId === t.id ? 'border-lime ring-1 ring-lime/40 bg-lime/5' : 'border-border hover:bg-muted/40'}`}
              data-testid={`template-option-${t.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm">{t.name}</p>
                <button onClick={(e) => handleDelete(t.id, e)} className="p-1 rounded hover:bg-red-50" title="Delete template" data-testid={`template-delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{t.title || '—'}{t.solution_area ? ` · ${t.solution_area}` : ''}{t.delivery_platform ? ` · ${t.delivery_platform}` : ''}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <Globe className="w-3 h-3 text-muted-foreground" />
                {(t.slots || []).length === 0 ? <span className="text-[11px] text-muted-foreground">No session slots</span> :
                  (t.slots || []).map((s, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{s.time_zone || 'TZ'}{s.start_time ? ` ${s.start_time}` : ''}</span>
                  ))}
              </div>
            </div>
          ))}
        </div>
        {selected && (
          <div className="border-t border-border pt-3 mt-2 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">New workshop title</label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={selected.title || selected.name} data-testid="template-title-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Start date</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="template-start-date-input" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleInstantiate} disabled={busy || !selectedId} className="gap-2" data-testid="template-instantiate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}Create Workshop
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WorkshopTemplateDialog;
